import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getOrders,
  getAdminProducts,
  markVendorSent,
} from '../services/adminApi';
import StatusBadge from '../components/StatusBadge';
import { buildVendorMessage, vendorWaLink } from '../../utils/whatsapp';
import { VENDOR } from '../../utils/store';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleString('en-IN', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/* Cancelled orders must never reach the vendor, and an unpaid one is
   stock going out of the door for free. Everything else is fair game,
   with "ready" meaning paid and not yet handed over. */
const FILTERS = [
  { id: 'ready', label: 'Ready to send' },
  { id: 'sent', label: 'Already sent' },
  { id: 'all', label: 'All live orders' },
];

/* Can this browser hand real files to another app?
   A wa.me link is text only — no attachment can ride along with it —
   so actual photos have to go through the system share sheet instead.
   Phones support this; most desktop browsers don't, which is why the
   button only appears when it will work. */
const canShareFiles = () => {
  if (typeof navigator === 'undefined' || !navigator.canShare) return false;

  try {
    const probe = new File(['x'], 'probe.png', { type: 'image/png' });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
};

/**
 * Dispatch desk.
 *
 * The supplier holds the stock and posts straight to the customer, so
 * every paid order has to be handed over to them once. This turns that
 * into one click: the message it sends is the parcel label — what to
 * pack, and the To/From block to print and stick on the box.
 */
export default function Vendor() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('ready');
  const [preview, setPreview] = useState(null); // order id whose text is open
  const [withPhotos, setWithPhotos] = useState(true);
  const [copied, setCopied] = useState(null);
  const [shareable] = useState(canShareFiles);

  const load = async () => {
    setLoading(true);
    const [{ data, error: loadError }, { data: rows }] = await Promise.all([
      getOrders(),
      getAdminProducts(),
    ]);
    setOrders(data);
    setProducts(rows || []);
    setError(loadError);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  /* order_items stores the name and quantity but not the photo, so the
     picture has to come from the catalogue. Matched on the product
     uuid, falling back to the code for rows written before product_id
     was recorded. */
  const imageFor = useMemo(() => {
    const byId = new Map(products.map((p) => [p.id, p.image_url]));
    const byCode = new Map(products.map((p) => [p.product_code, p.image_url]));

    return (item) =>
      byId.get(item.product_id) || byCode.get(item.product_code) || null;
  }, [products]);

  const photosFor = (order) =>
    (order.order_items || []).map((item) => ({
      name: item.product_name,
      url: imageFor(item),
    }));

  const messageFor = (order) =>
    buildVendorMessage(order, withPhotos ? photosFor(order) : []);

  const dispatchable = orders.filter(
    (o) => o.order_status !== 'cancelled' && o.payment_status === 'paid'
  );

  const shown = dispatchable.filter((o) => {
    if (filter === 'ready') return !o.vendor_sent_at;
    if (filter === 'sent') return !!o.vendor_sent_at;
    return true;
  });

  const send = async (order) => {
    const url = vendorWaLink(messageFor(order));

    if (!url) {
      alert(
        "The vendor's WhatsApp number isn't set yet. Add it to VENDOR.phone " +
          'in src/utils/store.js.'
      );
      return;
    }

    window.open(url, '_blank');

    // Optimistic: WhatsApp is open, so from here it's been handed over.
    setOrders((prev) =>
      prev.map((o) =>
        o.id === order.id
          ? { ...o, vendor_sent_at: new Date().toISOString() }
          : o
      )
    );

    const { error: saveError } = await markVendorSent(order.id);
    if (saveError) {
      console.warn(
        'Could not record the dispatch — run backend/sql/vendor.sql.',
        saveError
      );
    }
  };

  /* Downloaded photos, kept per order.

     Safari in particular only allows navigator.share() while the tap
     that triggered it is still "fresh", and awaiting a download uses
     that up. So the first tap fetches and caches; if the share is
     refused for that reason, the second tap shares instantly from the
     cache. */
  const fileCache = useRef(new Map());

  const filesFor = async (order) => {
    if (fileCache.current.has(order.id)) return fileCache.current.get(order.id);

    const wanted = photosFor(order).filter((p) => p.url);

    const files = (
      await Promise.all(
        wanted.map(async (photo, index) => {
          try {
            const response = await fetch(photo.url);
            if (!response.ok) return null;

            const blob = await response.blob();
            const extension = (blob.type.split('/')[1] || 'jpg').split('+')[0];

            return new File(
              [blob],
              `${index + 1}-${photo.name.replace(/[^\w-]+/g, '-')}.${extension}`,
              { type: blob.type || 'image/jpeg' }
            );
          } catch {
            // Usually the image host refusing a cross-origin read.
            return null;
          }
        })
      )
    ).filter(Boolean);

    fileCache.current.set(order.id, files);
    return files;
  };

  const [sharing, setSharing] = useState(null);

  const shareWithPhotos = async (order) => {
    setSharing(order.id);
    const files = await filesFor(order);
    setSharing(null);

    if (files.length === 0) {
      alert(
        "None of this order's photos could be downloaded, so there is " +
          'nothing to attach. Use "Send to vendor" for the text.'
      );
      return;
    }

    /* Text goes along as the caption. WhatsApp keeps it on a single
       image; with several it may drop it, so the label is still sent
       properly by the text button — this is the photos. */
    const payload = { files, text: messageFor(order) };

    if (!navigator.canShare?.(payload)) {
      alert('This browser will not share these files. Try it from your phone.');
      return;
    }

    try {
      await navigator.share(payload);
    } catch (shareError) {
      // The share sheet being dismissed is not a failure.
      if (shareError?.name === 'AbortError') return;

      alert(
        shareError?.name === 'NotAllowedError'
          ? 'The photos are ready now — tap "Send with photos" once more.'
          : `Could not share: ${shareError?.message || shareError}`
      );
    }
  };

  const copy = async (order) => {
    try {
      await navigator.clipboard.writeText(messageFor(order));
      setCopied(order.id);
      setTimeout(() => setCopied(null), 1800);
    } catch {
      alert('Could not copy — select the text below and copy it by hand.');
    }
  };

  if (loading) return <div className="admin-table-state">Loading orders…</div>;

  return (
    <div>
      <div className="page-head">
        <h1>Vendor Dispatch</h1>
        <div className="filter-pills">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              className={`pill ${filter === f.id ? 'active' : ''}`}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
              {f.id === 'ready' &&
                ` (${dispatchable.filter((o) => !o.vendor_sent_at).length})`}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <p className="admin-error">Failed to load orders: {error.message}</p>
      )}

      {!VENDOR.phone && (
        <p className="admin-error">
          No vendor WhatsApp number set — nothing can be sent yet. Add it to{' '}
          <code>VENDOR.phone</code> in <code>src/utils/store.js</code>.
        </p>
      )}

      <p className="hint">
        Only paid, uncancelled orders appear here. The message is the parcel
        label: what to pack, then the To/From block for the vendor to print.
        {shareable
          ? ' “With photos” attaches the real image files through your phone’s share sheet — you pick the vendor’s chat there.'
          : ' To attach real photos rather than links, open this page on your phone — desktop browsers can’t hand files to WhatsApp.'}
      </p>

      <label className="check" style={{ margin: '12px 0 18px' }}>
        <input
          type="checkbox"
          checked={withPhotos}
          onChange={(e) => setWithPhotos(e.target.checked)}
        />
        Include product photo links
      </label>

      {shown.length === 0 ? (
        <div className="admin-table-state">
          {filter === 'ready'
            ? 'Nothing waiting — every paid order has been sent to the vendor.'
            : filter === 'sent'
              ? 'Nothing has been sent to the vendor yet.'
              : 'No paid orders yet.'}
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Order #</th>
                <th>Ship to</th>
                <th>Items</th>
                <th>Value</th>
                <th>Vendor</th>
                <th style={{ width: '250px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((o) => (
                <tr key={o.id}>
                  <td className="cell-title">{o.order_number}</td>
                  <td>
                    <div className="cell-title">{o.customer_name}</div>
                    <div className="cell-sub">{o.customer_phone}</div>
                  </td>
                  <td>
                    <ul className="order-items">
                      {(o.order_items || []).map((it) => (
                        <li key={it.id}>
                          <span>{it.product_name}</span>
                          <span>× {it.quantity}</span>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="cell-title">{inr(o.final_amount)}</td>
                  <td>
                    {o.vendor_sent_at ? (
                      <>
                        <StatusBadge status="completed" />
                        <div className="cell-sub">
                          {fmtDate(o.vendor_sent_at)}
                        </div>
                      </>
                    ) : (
                      <StatusBadge status="pending" />
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-edit"
                        onClick={() => send(o)}
                        disabled={!VENDOR.phone}
                        title={
                          VENDOR.phone
                            ? 'Open WhatsApp with the dispatch details'
                            : 'Set VENDOR.phone in src/utils/store.js'
                        }
                      >
                        {o.vendor_sent_at ? '↻ Send again' : '📦 Send to vendor'}
                      </button>

                      {/* Real image files, via the system share sheet.
                          You pick the vendor's chat yourself — a share
                          sheet can't be pre-addressed the way a wa.me
                          link can. */}
                      {shareable && (
                        <button
                          className="btn-edit"
                          onClick={() => shareWithPhotos(o)}
                          disabled={sharing === o.id}
                          title="Attach the actual product photos"
                        >
                          {sharing === o.id ? 'Fetching…' : '🖼 With photos'}
                        </button>
                      )}

                      <button className="btn-view" onClick={() => copy(o)}>
                        {copied === o.id ? 'Copied ✓' : 'Copy'}
                      </button>
                      <button
                        className="btn-view"
                        onClick={() =>
                          setPreview(preview === o.id ? null : o.id)
                        }
                      >
                        {preview === o.id ? 'Hide' : 'Preview'}
                      </button>
                    </div>

                    {/* Exactly what WhatsApp will carry — no surprises
                        once it's photocopied onto a box. */}
                    {preview === o.id && (
                      <pre className="vendor-preview">{messageFor(o)}</pre>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
